import React, { useEffect, useState, useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import classnames from 'classnames';
import * as Yup from 'yup';
import { Formik, Form, ErrorMessage, useField } from 'formik';
import { getMfaManagementUserById, putMfaManagementUser, removeTrustedDevice } from '../../../services/mfaManagement.service';
import { AppContext } from '../../../AppContext';
import Loading from '../../Loading';

const styles = theme => ({
  formTextFieldInput: {
    padding: 15,
  },
  formTextFieldNotchedOutline: {
    '& fieldset': {
      borderColor: 'var(--jmi-blue) !important',
    },
  },
  errorMessage: {
    color: theme.palette.secondary.main,
    textAlign: 'center',
  },
});

const FormTextField = withStyles(styles)(({ classes, label, isReadOnly, ...props }) => {
  const { name } = props;
  const [field] = useField(props);

  return (
    <TextField
      name={name}
      label={label}
      autoComplete={name}
      margin="none"
      variant="outlined"
      fullWidth
      disabled={isReadOnly}
      inputProps={{
        className: classes.formTextFieldInput,
      }}
      InputProps={{
        classes: {
          focused: classes.formTextFieldNotchedOutline,
        },
      }}
      {...props}
      {...field}
    />
  );
});

const FormControlWrapper = ({ children, ...props }) => (
  <Grid item xs={12} sm={6} md={4} {...props}>
    {children}
  </Grid>
);

const formInitialValues = {
  userId: '',
  username: '',
  email: '',
  phoneNumber: '',
  role: '',
  twoFactorEnabled: false,
  inspectorId: null,
  qaId: null,
  callCenterUserId: null,
  underwriterId: null,
  clientId: null,
  trustedDevices: [],
};

const formValidationSchema = Yup.object().shape({
  userId: Yup.string().required('Required'),
  username: Yup.string().required('Required'),
  email: Yup.string().email().required('Required'),
  phoneNumber: Yup.string().notRequired(),
  role: Yup.string().required('Required'),
  twoFactorEnabled: Yup.bool(),
});

const getInitialFormValues = (initValues, existingValues = {}) => {
  return Object.keys(initValues).reduce((acc, key) => {
    const existingValue = existingValues?.[key];
    acc[key] = [null, undefined].includes(existingValue) ? initValues[key] : existingValue;
    return acc;
  }, existingValues || {});
};

export const MfaManagementUsersDetail = withStyles(styles)(({ classes, match, history }) => {
  const { userId } = match.params;
  const [profile, setProfile] = useState(null);
  const { dispatch } = useContext(AppContext);

  const getExistingProfile = async () => {
    try {
      const data = await getMfaManagementUserById(userId);
      const role = data.role || data.Role || data.roleName || data.RoleName || '';
      setProfile({
        userId: data.userId || data.UserId,
        username: data.username || data.Username || '',
        email: data.email || data.Email || '',
        twoFactorEnabled: !!data.twoFactorEnabled || !!data.TwoFactorEnabled,
        role: role,
        phoneNumber: data.phoneNumber || data.PhoneNumber || '',
        inspectorId: data.inspectorId || data.InspectorId || null,
        qaId: data.qaId || data.QaId || null,
        callCenterUserId: data.callCenterUserId || data.CallCenterUserId || null,
        underwriterId: data.underwriterId || data.UnderwriterId || null,
        clientId: data.clientId || data.ClientId || null,
        trustedDevices: data.trustedDevices || data.TrustedDevices || [],
      });
    } catch (error) {
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: 'Error loading MFA user',
          variant: 'error',
        },
      });
    }
  };

  const showSnackbar = (message, variant) => {
    dispatch({
      type: 'SET_SNACKBAR',
      snackbar: { isOpen: true, message, variant },
    });
  };

  const getRoleNavigationPath = (role, userId) => {
    if (!role) return null;
    
    const roleMap = {
      'callcenter': `/users/callcenter/${userId}`,
      'call center': `/users/callcenter/${userId}`,
      'fieldrep': `/users/field-reps/${userId}`,
      'field rep': `/users/field-reps/${userId}`,
      'fieldmanager': `/users/field-manager/${userId}`,
      'field manager': `/users/field-manager/${userId}`,
      'client': `/users/client/${userId}`,
      'admin': `/users/admin/${userId}`,
      'qa': `/users/qa/${userId}`,
      'underwriter': `/users/underwriter/${userId}`,
    };
    
    const normalizedRole = role.toLowerCase();
    console.log('Getting path for role:', role, 'Normalized:', normalizedRole, 'Result:', roleMap[normalizedRole]);
    return roleMap[normalizedRole] || null;
  };

  const handleNavigateToRoleDetail = () => {
    if (!profile || !profile.role) return;

    const role = profile.role.toLowerCase();
    let roleSpecificId = null;
    
    // Get the appropriate ID based on role
    switch (role) {
      case 'admin':
        roleSpecificId = profile.userId;
        break;
      case 'client':
        roleSpecificId = profile.clientId || profile.userId;
        break;
      case 'fieldrep':
      case 'field rep':
        roleSpecificId = profile.inspectorId;
        break;
      case 'qa':
        roleSpecificId = profile.qaId;
        break;
      case 'callcenter':
      case 'call center':
        roleSpecificId = profile.callCenterUserId;
        break;
      case 'underwriter':
        roleSpecificId = profile.underwriterId;
        break;
      default:
        showSnackbar('Unknown role type', 'error');
        return;
    }
    
    if (!roleSpecificId) {
      showSnackbar('Role detail ID not found', 'error');
      return;
    }
    
    const path = getRoleNavigationPath(role, roleSpecificId);
    if (path) {
      history.push(path);
    }
  };

  const handleFormSubmission = async formValues => {
    // Check if trying to enable MFA without a phone number
    if (formValues.twoFactorEnabled && !formValues.phoneNumber) {
      const confirmEnable = window.confirm(
        'This user has no phone number for MFA. Are you sure you want to enable Two-Factor Authentication?'
      );
      if (!confirmEnable) {
        return;
      }
    }

    try {
      await putMfaManagementUser({
        userId: formValues.userId,
        email: formValues.email,
        phoneNumber: formValues.phoneNumber,
        twoFactorEnabled: formValues.twoFactorEnabled,
      });
      showSnackbar('MFA settings updated!', 'success');
      history.push('/user-management/mfa-management');
    } catch (error) {
      showSnackbar(error?.message || 'An error occurred. Please try again.', 'error');
    }
  };

  useEffect(() => {
    getExistingProfile();
  }, [userId]);

  return (
    <div className={'grid-container'} style={{ position: 'relative' }}>
      {!profile && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 1,
          }}
        >
          <Loading size={100} style={{ position: 'absolute', top: 200, color: 'white' }} />
        </div>
      )}

      <Formik
        initialValues={getInitialFormValues(formInitialValues, profile)}
        onSubmit={handleFormSubmission}
        validationSchema={formValidationSchema}
        enableReinitialize
      >
        {formik => (
          <Form>
            <Grid container spacing={3}>
              {/* User Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" style={{ marginBottom: 10, color: 'var(--jmi-blue)' }}>
                  User Information
                </Typography>
              </Grid>

              <FormControlWrapper>
                <FormTextField name="username" label="Username" formik={formik} isReadOnly />
                <span className={classes.errorMessage}>
                  <ErrorMessage name="username" />
                </span>
              </FormControlWrapper>

              <FormControlWrapper>
                <FormTextField name="role" label="Role" formik={formik} isReadOnly />
                <span className={classes.errorMessage}>
                  <ErrorMessage name="role" />
                </span>
                {profile && profile.role && getRoleNavigationPath(profile.role, profile.userId) && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleNavigateToRoleDetail}
                    style={{ marginTop: 10 }}
                  >
                    View {profile.role} Details
                  </Button>
                )}
              </FormControlWrapper>

              <FormControlWrapper></FormControlWrapper>

              {/* Two-Factor Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" style={{ marginTop: 20, marginBottom: 10, color: 'var(--jmi-blue)' }}>
                  Two-Factor Information
                </Typography>
              </Grid>

              <FormControlWrapper>
                <FormTextField name="email" label="Email" formik={formik} />
                <span className={classes.errorMessage}>
                  <ErrorMessage name="email" />
                </span>
              </FormControlWrapper>

              <FormControlWrapper>
                <FormTextField name="phoneNumber" label="Phone Number" formik={formik} />
                <span className={classes.errorMessage}>
                  <ErrorMessage name="phoneNumber" />
                </span>
              </FormControlWrapper>

              <FormControlWrapper>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.twoFactorEnabled}
                      onChange={e => formik.setFieldValue('twoFactorEnabled', e.target.checked)}
                      name="twoFactorEnabled"
                      color="primary"
                    />
                  }
                  label="Two Factor Enabled"
                />
              </FormControlWrapper>
            </Grid>

            {/* Trusted Devices Section */}
            {profile && profile.trustedDevices && profile.trustedDevices.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" style={{ marginTop: 30, marginBottom: 10, color: 'var(--jmi-blue)' }}>
                    Trusted Devices ({profile.trustedDevices.length})
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd' }}>
                        <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Device</th>
                        <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Created</th>
                        <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Expires</th>
                        <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Status</th>
                        <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profile.trustedDevices.map((device) => (
                        <tr key={device.deviceId || device.DeviceId} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px', textAlign: 'left' }}>
                            {device.label || device.Label || 'Unknown Device'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'left', color: '#666' }}>
                            {new Date(device.createdAt || device.CreatedAt).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'left', color: '#666' }}>
                            {new Date(device.expiresAt || device.ExpiresAt).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'left' }}>
                            {(device.isExpired || device.IsExpired) ? (
                              <span style={{ color: '#d32f2f', fontWeight: 600 }}>Expired</span>
                            ) : (
                              <span style={{ color: '#4caf50', fontWeight: 600 }}>Active</span>
                            )}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to remove this device?')) {
                                  try {
                                    await removeTrustedDevice(profile.userId, device.deviceId || device.DeviceId);
                                    showSnackbar('Device removed successfully', 'success');
                                    await getExistingProfile();
                                  } catch (error) {
                                    showSnackbar('Failed to remove device', 'error');
                                  }
                                }
                              }}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Grid>
              </>
            )}

            <div style={{ width: '100%', textAlign: 'center', marginTop: 30 }}>
              {formik.isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => history.push('/user-management/mfa-management')}
                    style={{ marginRight: 10 }}
                  >
                    {formik.dirty ? 'Cancel' : 'Back'}
                  </Button>
                  <Button
                    type="submit"
                    size="medium"
                    color="primary"
                    variant="contained"
                    onClick={formik.submitForm}
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
});

